import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getMaster, getProject, createMaster, updateMaster, MASTER_STATUSES, MASTER_FORMATS, FRAME_RATES, LANGUAGES, errorMessage } from '../services/api';
import { toast } from 'sonner';
import { useFieldErrors } from '../lib/useFieldErrors';
import { FieldError } from '../components/FieldError';
import { ArrowLeft, Loader2, Plus, X, Volume2, Subtitles, AlertTriangle, HardDrive } from 'lucide-react';

export default function MasterForm() {
    const { id, projectId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const targetProjectId = projectId || searchParams.get('project_id');
    const duplicateFromId = searchParams.get('duplicate');
    
    const [loading, setLoading] = useState(isEdit || !!duplicateFromId);
    const [saving, setSaving] = useState(false);
    const [project, setProject] = useState(null);
    const { errors, fail, clearError, resetErrors, fieldProps } = useFieldErrors();
    const [formData, setFormData] = useState({
        name: '',
        disk_number: '',
        duration_tc: '00:00:00:00',
        format: '2K flat',
        frame_rate: '24p',
        audio_tracks: [],
        subtitle_tracks: [],
        encrypted: false,
        version: '1',
        previous_master_name: '',
        status: 'en_stock'
    });
    const [newAudioLang, setNewAudioLang] = useState('');
    const [newSubLangs, setNewSubLangs] = useState({});
    
    // State for disk replacement confirmation
    const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
    const [existingMaster, setExistingMaster] = useState(null);

    useEffect(() => {
        if (isEdit) {
            fetchMaster();
        } else if (duplicateFromId) {
            fetchDuplicate();
        } else if (targetProjectId) {
            fetchProject(targetProjectId);
        } else {
            navigate('/projects');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [id, targetProjectId, duplicateFromId]);

    const fetchDuplicate = async () => {
        try {
            const response = await getMaster(duplicateFromId);
            const master = response.data;
            let audioTracks = (master.audio_tracks || []).map(t => ({
                ...t,
                subtitles: t.subtitles || []
            }));
            setFormData({
                name: master.name || '',
                disk_number: '',
                duration_tc: master.duration_tc || '00:00:00:00',
                format: master.format || '2K flat',
                frame_rate: master.frame_rate || '24p',
                audio_tracks: audioTracks,
                subtitle_tracks: [],
                encrypted: master.encrypted || false,
                version: master.version || '1',
                previous_master_name: '',
                status: 'en_stock'
            });
            fetchProject(master.project_id);
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement du master source'));
            navigate('/masters');
        } finally {
            setLoading(false);
        }
    };

    const fetchMaster = async () => {
        try {
            const response = await getMaster(id);
            const master = response.data;
            // Migrate: attach standalone subtitle_tracks to audio_tracks if needed
            let audioTracks = (master.audio_tracks || []).map(t => ({
                ...t,
                subtitles: t.subtitles || []
            }));
            const standaloneSubtitles = master.subtitle_tracks || [];
            if (standaloneSubtitles.length > 0 && audioTracks.length > 0) {
                // Attach old-format subtitles to first audio track if no audio has subtitles yet
                const anyHasSubs = audioTracks.some(t => t.subtitles.length > 0);
                if (!anyHasSubs) {
                    audioTracks[0].subtitles = standaloneSubtitles.map(s => s.language);
                }
            }
            setFormData({
                name: master.name || '',
                disk_number: master.disk_number || '',
                duration_tc: master.duration_tc || '00:00:00:00',
                format: master.format || '2K flat',
                frame_rate: master.frame_rate || '24p',
                audio_tracks: audioTracks,
                subtitle_tracks: [],
                encrypted: master.encrypted || false,
                version: master.version || '1',
                previous_master_name: master.previous_master_name || '',
                status: master.status || 'en_stock'
            });
            await fetchProject(master.project_id);
        } catch (error) {
            if (!error._isAuthError) toast.error(errorMessage(error, 'Erreur lors du chargement du master'));
            navigate('/masters');
        } finally {
            setLoading(false);
        }
    };

    const fetchProject = async (pid) => {
        try {
            const response = await getProject(pid);
            setProject(response.data);
        } catch (error) {
            toast.error(errorMessage(error, 'Projet non trouvé'));
            navigate('/projects');
        } finally {
            if (!isEdit) setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        clearError(name);
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSwitchChange = (name, checked) => {
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const addAudioTrack = () => {
        if (!newAudioLang) return;
        setFormData(prev => ({
            ...prev,
            audio_tracks: [...prev.audio_tracks, { language: newAudioLang, type: 'audio', subtitles: [] }]
        }));
        setNewAudioLang('');
    };

    const removeAudioTrack = (index) => {
        setFormData(prev => ({
            ...prev,
            audio_tracks: prev.audio_tracks.filter((_, i) => i !== index)
        }));
        setNewSubLangs(prev => {
            const copy = { ...prev };
            delete copy[index];
            return copy;
        });
    };

    const addSubtitleToAudio = (audioIndex) => {
        const lang = newSubLangs[audioIndex];
        if (!lang) return;
        setFormData(prev => {
            const tracks = [...prev.audio_tracks];
            tracks[audioIndex] = {
                ...tracks[audioIndex],
                subtitles: [...(tracks[audioIndex].subtitles || []), lang]
            };
            return { ...prev, audio_tracks: tracks };
        });
        setNewSubLangs(prev => ({ ...prev, [audioIndex]: '' }));
    };

    const removeSubtitleFromAudio = (audioIndex, subIndex) => {
        setFormData(prev => {
            const tracks = [...prev.audio_tracks];
            tracks[audioIndex] = {
                ...tracks[audioIndex],
                subtitles: tracks[audioIndex].subtitles.filter((_, i) => i !== subIndex)
            };
            return { ...prev, audio_tracks: tracks };
        });
    };

    const validateTC = (tc) => {
        const tcRegex = /^([0-9]{2}):([0-5][0-9]):([0-5][0-9]):([0-9]{2})$/;
        return tcRegex.test(tc);
    };

    const submitMaster = async (replaceExisting = false) => {
        setSaving(true);
        setReplaceDialogOpen(false);

        // Build subtitle_tracks flat list from audio_tracks for backward compat
        const allSubtitles = [];
        formData.audio_tracks.forEach(track => {
            (track.subtitles || []).forEach(lang => {
                if (!allSubtitles.find(s => s.language === lang)) {
                    allSubtitles.push({ language: lang, type: 'subtitle' });
                }
            });
        });

        try {
            const dataToSend = {
                ...formData,
                subtitle_tracks: allSubtitles,
                replace_existing: replaceExisting
            };

            if (isEdit) {
                await updateMaster(id, dataToSend);
                toast.success('Master mis à jour');
                navigate(`/masters/${id}`);
            } else {
                const response = await createMaster(targetProjectId, dataToSend);
                if (replaceExisting) {
                    toast.success('Master créé - L\'ancien disque a été marqué comme écrasé');
                } else {
                    toast.success('Master créé');
                }
                navigate(`/masters/${response.data.id}`);
            }
        } catch (error) {
            // Check if it's a disk conflict error
            if (error.response?.status === 409) {
                const detail = error.response.data.detail;
                if (detail?.action_required === 'confirm_replace') {
                    setExistingMaster(detail.existing_master);
                    setReplaceDialogOpen(true);
                    return;
                }
            }
            toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            fail('name', 'Le nom du master est requis');
            return;
        }

        if (!formData.disk_number.trim()) {
            fail('disk_number', 'Le numéro de disque est requis');
            return;
        }

        if (!validateTC(formData.duration_tc)) {
            fail('duration_tc', 'Format de durée invalide (HH:MM:SS:FF)');
            return;
        }

        await submitMaster(false);
    };

    const handleConfirmReplace = () => {
        submitMaster(true);
    };

    if (loading) {
        return (
            <Layout>
                <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-[600px]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="master-form">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="icon" aria-label="Retour" 
                        onClick={() => navigate(isEdit ? `/masters/${id}` : `/projects/${targetProjectId}`)}
                        data-testid="back-button"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            {isEdit ? 'MODIFIER LE MASTER' : 'NOUVEAU MASTER'}
                        </h1>
                        {project && (
                            <p className="text-muted-foreground">Projet: {project.title}</p>
                        )}
                    </div>
                </div>

                {/* Form */}
                <Card className="bg-card border-border">
                    <CardContent className="pt-6">
                        <form noValidate onSubmit={handleSubmit} className="space-y-6">
                            {/* Name */}
                            <div className="space-y-2">
                                <Label htmlFor="name">Nom du master *</Label>
                                <Input
                                    id="name"
                                    {...fieldProps('name')}
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Ex: Terminator_FTR_F-178_JA-FR_FR_51_2K_EZ_20251229_MEF_IOP_OV"
                                    className="font-mono text-sm"
                                    required
                                    data-testid="name-input"
                                />
                                <FieldError id="name" message={errors['name']} />
                            </div>

                            {/* Disk Number & Duration */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="disk_number">Numéro du disque *</Label>
                                    <Input
                                        id="disk_number"
                                        {...fieldProps('disk_number')}
                                        name="disk_number"
                                        value={formData.disk_number}
                                        onChange={handleChange}
                                        placeholder="Ex: DD-001"
                                        required
                                        data-testid="disk-number-input"
                                    />
                                    <FieldError id="disk_number" message={errors['disk_number']} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="duration_tc">Durée (HH:MM:SS:FF) *</Label>
                                    <Input
                                        id="duration_tc"
                                        {...fieldProps('duration_tc')}
                                        name="duration_tc"
                                        value={formData.duration_tc}
                                        onChange={handleChange}
                                        placeholder="01:45:30:00"
                                        className="font-mono"
                                        required
                                        data-testid="duration-input"
                                    />
                                    <FieldError id="duration_tc" message={errors['duration_tc']} />
                                </div>
                            </div>

                            {/* Format & Frame Rate */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="f-format">Format *</Label>
                                    <Select value={formData.format} onValueChange={(v) => handleSelectChange('format', v)}>
                                        <SelectTrigger id="f-format" data-testid="format-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MASTER_FORMATS.map(format => (
                                                <SelectItem key={format} value={format}>{format}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="f-frequence-d-image">Fréquence d'image *</Label>
                                    <Select value={formData.frame_rate} onValueChange={(v) => handleSelectChange('frame_rate', v)}>
                                        <SelectTrigger id="f-frequence-d-image" data-testid="frame-rate-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {FRAME_RATES.map(rate => (
                                                <SelectItem key={rate} value={rate}>{rate}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Audio Tracks with Subtitles */}
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2">
                                    <Volume2 className="w-4 h-4" />
                                    Pistes audio & sous-titres
                                </Label>

                                {formData.audio_tracks.map((track, audioIdx) => (
                                    <div key={audioIdx} className="p-3 rounded-sm bg-secondary/30 border border-border space-y-2" data-testid={`audio-track-${audioIdx}`}>
                                        <div className="flex items-center justify-between">
                                            <Badge variant="secondary" className="text-sm">
                                                <Volume2 className="w-3 h-3 mr-1" />
                                                {track.language}
                                            </Badge>
                                            <button
                                                type="button"
                                                onClick={() => removeAudioTrack(audioIdx)}
                                                aria-label={`Retirer la piste audio ${track.language}`}
                                                className="inline-flex items-center justify-center w-6 h-6 touch:w-11 touch:h-11 -m-1 touch:-m-2.5 rounded-sm text-muted-foreground hover:text-destructive-text hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {/* Subtitles for this audio */}
                                        {(track.subtitles || []).length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 pl-4">
                                                {track.subtitles.map((subLang, subIdx) => (
                                                    <Badge key={subIdx} variant="outline" className="text-xs pr-1">
                                                        <Subtitles className="w-2.5 h-2.5 mr-1" />
                                                        ST {subLang}
                                                        <button
                                                            type="button"
                                                            onClick={() => removeSubtitleFromAudio(audioIdx, subIdx)}
                                                            aria-label={`Retirer le sous-titre ${subLang} de la piste ${track.language}`}
                                                            className="inline-flex items-center justify-center w-6 h-6 touch:w-11 touch:h-11 -my-1.5 -mr-1 touch:-my-3.5 touch:-mr-2.5 ml-0.5 rounded-sm hover:text-destructive-text hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}

                                        {/* Add subtitle to this audio */}
                                        <div className="flex gap-2 pl-4">
                                            <Select
                                                value={newSubLangs[audioIdx] || ''}
                                                onValueChange={(v) => setNewSubLangs(prev => ({ ...prev, [audioIdx]: v }))}
                                            >
                                                <SelectTrigger className="w-[180px] h-8 text-xs" data-testid={`sub-lang-select-${audioIdx}`}>
                                                    <SelectValue placeholder="+ Sous-titre" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {LANGUAGES.map(lang => (
                                                        <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-8"
                                                onClick={() => addSubtitleToAudio(audioIdx)}
                                                disabled={!newSubLangs[audioIdx]}
                                                aria-label={`Ajouter le sous-titre à la piste ${track.language}`}
                                            >
                                                <Plus className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                {/* Add audio track */}
                                <div className="flex gap-2">
                                    <Select value={newAudioLang} onValueChange={setNewAudioLang}>
                                        <SelectTrigger className="w-[200px]" data-testid="audio-lang-select">
                                            <SelectValue placeholder="Ajouter une piste audio" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {LANGUAGES.map(lang => (
                                                <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button type="button" variant="outline" onClick={addAudioTrack} disabled={!newAudioLang} aria-label="Ajouter la piste audio">
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Version & Previous Master */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="version">Version</Label>
                                    <Input
                                        id="version"
                                        name="version"
                                        value={formData.version}
                                        onChange={handleChange}
                                        placeholder="1"
                                        data-testid="version-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="previous_master_name">Master précédent</Label>
                                    <Input
                                        id="previous_master_name"
                                        name="previous_master_name"
                                        value={formData.previous_master_name}
                                        onChange={handleChange}
                                        placeholder="Si réutilisation d'un disque"
                                        data-testid="previous-master-input"
                                    />
                                </div>
                            </div>

                            {/* Status */}
                            <div className="space-y-2">
                                <Label htmlFor="f-statut">Statut</Label>
                                <Select value={formData.status} onValueChange={(v) => handleSelectChange('status', v)}>
                                    <SelectTrigger id="f-statut" data-testid="status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(MASTER_STATUSES).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Encrypted Switch */}
                            <div className="flex items-center justify-between p-4 rounded-sm bg-secondary/30">
                                <Label htmlFor="encrypted" className="cursor-pointer">DCP Crypté</Label>
                                <Switch
                                    id="encrypted"
                                    checked={formData.encrypted}
                                    onCheckedChange={(checked) => handleSwitchChange('encrypted', checked)}
                                    data-testid="encrypted-switch"
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-4 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate(isEdit ? `/masters/${id}` : `/projects/${targetProjectId}`)}
                                    className="flex-1"
                                >
                                    Annuler
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1"
                                    data-testid="submit-button"
                                >
                                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    {isEdit ? 'Enregistrer' : 'Créer le master'}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>

            {/* Replace Disk Confirmation Dialog */}
            <AlertDialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen}>
                <AlertDialogContent className="max-w-lg">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-tone-alerte">
                            <AlertTriangle className="w-5 h-5" />
                            Numéro de disque existant
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4">
                                <p>
                                    Le disque <strong className="text-foreground">{formData.disk_number}</strong> est déjà utilisé par un autre master.
                                </p>
                                
                                {existingMaster && (
                                    <div className="p-4 rounded-lg bg-secondary/50 border border-border">
                                        <div className="flex items-start gap-3">
                                            <HardDrive className="w-5 h-5 text-muted-foreground mt-0.5" />
                                            <div className="space-y-1 text-sm">
                                                <p className="font-medium text-foreground">Master actuel :</p>
                                                <p className="font-mono text-xs text-muted-foreground break-all">
                                                    {existingMaster.name}
                                                </p>
                                                <p className="text-muted-foreground">
                                                    Disque : {existingMaster.disk_number}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="p-3 rounded bg-tone-alerte-base/10 border border-tone-alerte-base/30 text-sm">
                                    <p className="font-medium text-tone-alerte mb-2">Si vous confirmez :</p>
                                    <ul className="space-y-1 text-muted-foreground">
                                        <li>• L'ancien master passera en statut <strong className="text-tone-ecrase">"Écrasé"</strong></li>
                                        <li>• Son numéro de disque sera modifié en <strong className="text-foreground">{formData.disk_number}-1</strong></li>
                                        <li>• Le nouveau master référencera l'ancien dans "Master précédent"</li>
                                    </ul>
                                </div>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleConfirmReplace}
                            className="bg-orange-700 hover:bg-orange-800 text-white"
                            data-testid="confirm-replace-button"
                        >
                            Confirmer le remplacement
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
